import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Tab, Tooltip } from '@mui/material';
import { TrendingDown, TrendingUp } from 'lucide-react';
import SafeHtml from '../components/common/SafeHtml';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { getAuthHeaders } from '../utils/apiUtils';
import { generateSectionExport } from '../utils/exportUtils';
import { processHistoryData, processHistorySection, analyzeSeverityTrend, isSavedHistoryDocument } from '../utils/historyProcessing';
import { selectHistoryDocuments, stripHistoryHtml } from '../utils/historyEvidence';

const EMPTY_HISTORY = 'History data will appear after you complete a Progress Note for this patient.';
const categories = [
  { label: 'Social History', sectionKey: 'socialHistory', summaryField: 'social_history_summary' },
  { label: 'Trauma History', sectionKey: 'traumaHistory', summaryField: 'trauma_history_summary' },
  { label: 'Past Psychiatric History', sectionKey: 'psychiatricHistory', summaryField: 'psychiatric_history_summary' },
  { label: 'Medication History', sectionKey: 'medications' },
  { label: 'Substance Use', sectionKey: 'substanceUse' },
  { label: 'Family History', sectionKey: 'familyHistory' },
  { label: 'Medical History', sectionKey: 'medicalHistory', summaryField: 'medical_history_summary', extraSection: 'allergies' },
];

const hasSummary = value => Object.values(value || {}).some(item =>
  typeof item?.summary === 'string' && item.summary.trim()
    && item.summary.trim().toLowerCase() !== 'no historical information');

function SeverityInfo({ item }) {
  if (!Number.isFinite(item?.severityRank)) return null;
  const trend = item.severityRankTrend;
  return <span className="severity-info">
    <span className="severity-rank">Severity: {item.severityRank}</span>
    {Number.isFinite(item.severityRankAverage) && item.severityRankAverage !== item.severityRank &&
      <span className="severity-average">Avg: {item.severityRankAverage}</span>}
    {trend === 'getting better' && <Tooltip title="Getting better" arrow><TrendingDown className="severity-trend-icon severity-trend-improving" /></Tooltip>}
    {trend === 'getting worse' && <Tooltip title="Getting worse" arrow><TrendingUp className="severity-trend-icon severity-trend-worsening" /></Tooltip>}
    {['trend unchanged', 'staying stable'].includes(trend) && <span className="severity-trend-stable">stable</span>}
  </span>;
}

export default function SubTabs({ activeSubTab = 0, handleSubTabChange, historySummary, patientDocuments, patientId, topSpacing }) {
  const { flags } = useFeatureFlags();
  const [sections, setSections] = useState(null);
  const currentHistory = isSavedHistoryDocument(historySummary) ? historySummary : null;
  const hasCurrentHistory = !!currentHistory;

  useEffect(() => {
    let cancelled = false;
    setSections(null);
    if (!hasCurrentHistory) return undefined;
    const load = async () => {
      try {
        const response = await fetch('/api/v3/configuration/sections', { headers: await getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!data.mainSections || typeof data.mainSections !== 'object' || Array.isArray(data.mainSections)) {
          throw new Error('Section configuration unavailable');
        }
        if (!cancelled) setSections(data.mainSections);
      } catch (error) {
        if (!cancelled) console.error('Error fetching history sections:', error);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [hasCurrentHistory, patientId]);

  const documents = useMemo(() => selectHistoryDocuments(patientDocuments || []), [patientDocuments]);
  const tabs = useMemo(() => {
    if (!sections || !currentHistory) return [];
    const sectionHtml = sectionKey => {
      const section = sections[sectionKey];
      if (!section?.primaryField || !section.dataField) return '';
      const field = section.dataField;
      // Preserve encounter chronology when collapsing repeated source rows.
      // Export only assembled values; do not reuse export's legacy adapter.
      const history = processHistoryData(documents.map(doc => ({
        ...doc[field], documentDate: doc.documentDate, sourceDocumentId: doc.id,
      })), item => item[section.primaryField] || (field === 'family_history' && item.name) || item.id)
        .map(item => field === 'family_history' && !item[section.primaryField] && item.name
          ? { ...item, [section.primaryField]: item.name } : item);
      const values = processHistorySection(section, currentHistory[field], history);
      const html = generateSectionExport(section, { [field]: values }, [])?.[field]?.data || '';
      return stripHistoryHtml(html) ? html : '';
    };
    return categories.map(category => {
      const section = sections[category.sectionKey];
      const summaryDocuments = [currentHistory, ...(patientDocuments || []).filter(doc => doc.id !== currentHistory.id)];
      const summary = category.summaryField ? analyzeSeverityTrend(summaryDocuments, category.summaryField) : null;
      const rows = Array.isArray(section?.sortOrder) && section.sortOrder.length && hasSummary(summary)
        ? section.sortOrder.map(field => {
          const item = Object.values(summary).find(entry => entry?.originalName === field || entry?.name === field);
          return { field, item };
        }) : [];
      const html = category.summaryField ? '' : sectionHtml(category.sectionKey);
      const extraHtml = category.extraSection ? sectionHtml(category.extraSection) : '';
      return { ...category, rows, html, extraHtml };
    }).filter(tab => tab.rows.length || tab.html || tab.extraHtml);
  }, [sections, currentHistory, documents, patientDocuments]);

  if (!currentHistory) return <div className="history-summary-section">{EMPTY_HISTORY}</div>;
  if (!sections || !tabs.length) return null;

  const selected = Math.max(0, Math.min(activeSubTab, tabs.length - 1));
  const tab = tabs[selected];
  return <div className="patient-history-container userback-ignore">
    <div className="patient-subtabs-container">
      <Tabs value={selected} onChange={handleSubTabChange} className="patient-sub-tabs" scrollButtons="auto" sx={{ height: '32px' }} aria-label="Life Story categories">
        {tabs.map(item => <Tab key={item.label} label={item.label} className="patient-sub-tab" wrapped />)}
      </Tabs>
    </div>
    <div className={topSpacing ? 'session-history-spacing' : ''}>
      {tab.rows.length > 0 && <div className="history-summary-section">
        <ul className="history-summary-list">
          {tab.rows.map(({ field, item }) => <li key={field}>
            <strong>{item?.name || field}:</strong>
            {flags?.showSeverity && <SeverityInfo item={item} />}
            <div>{item?.summary || 'No historical information'}</div>
          </li>)}
        </ul>
      </div>}
      {tab.html && <div className="history-summary-section"><SafeHtml className="history-summary-list" html={tab.html} /></div>}
      {tab.extraHtml && <div className="history-summary-section"><SafeHtml className="history-summary-list" html={tab.extraHtml} /></div>}
    </div>
  </div>;
}
